import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types";
import { ObligationDocumentOption } from "../../../annualWheel/activityForm/ActivityContractSourceSection.types";
import { getDocumentBlockedTranslationKey, isDocumentAnalysable, toPreviewableDocument } from "./contractDocumentOptions";

/**
 * The contract's documents as pickable options, for any flow that asks the AI to read one. The endpoint
 * returns hasAccess rather than filtering, which is what lets an inaccessible document show disabled with
 * a reason instead of quietly disappearing.
 */
export function useContractDocumentOptions(contractId: string, selectedContractDocumentId: string | undefined) {
    const { translateString } = useTranslation();

    const { data, isLoading } = useDotLegalQuery<Array<ObligationDocumentOption>>(`/contract/${contractId}/documents/obligation-options`);
    const options = data ?? [];
    const selectedOption = options.find((option) => option.contractDocumentId === selectedContractDocumentId);

    const documentOptions: Array<DotLegalSelectOption> = options.map((option) => {
        const blockedKey = getDocumentBlockedTranslationKey(option);

        return {
            id: option.contractDocumentId,
            name: option.name,
            disabled: blockedKey !== undefined,
            hoverText: blockedKey ? translateString(blockedKey) : undefined,
        };
    });

    const selectedDocument: PreviewableDocument | undefined =
        selectedOption && isDocumentAnalysable(selectedOption) ? toPreviewableDocument(selectedOption, contractId) : undefined;

    return {
        isLoading,
        documentOptions,
        selectedDocument,
    };
}
